import { StyleSheet, Text, type TextProps } from "react-native";

import { FontSize } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";

type Variant =
  | "display"
  | "title"
  | "subtitle"
  | "body"
  | "bodyStrong"
  | "label"
  | "caption"
  | "link";

type Tone =
  | "default"
  | "secondary"
  | "muted"
  | "tint"
  | "onTint"
  | "danger"
  | "success";

export type ThemedTextProps = TextProps & {
  variant?: Variant;
  tone?: Tone;
  center?: boolean;
};

/**
 * Per-variant caps on OS/browser font scaling. We never disable scaling
 * (accessibility); we only bound it so large accessibility settings can't
 * shatter tight layouts. Reading text (body/titles) gets generous headroom;
 * dense/compact UI (labels/captions used in chips, tabs, axes) is capped lower.
 * Any call site can override by passing `maxFontSizeMultiplier` explicitly.
 */
const VARIANT_MAX_SCALE: Record<Variant, number> = {
  display: 1.4,
  title: 1.4,
  subtitle: 1.5,
  body: 1.6,
  bodyStrong: 1.6,
  label: 1.4,
  caption: 1.4,
  link: 1.5,
};

export function ThemedText({
  variant = "body",
  tone = "default",
  center,
  style,
  maxFontSizeMultiplier,
  ...rest
}: ThemedTextProps) {
  const c = useTheme();
  const color =
    tone === "secondary"
      ? c.textSecondary
      : tone === "muted"
        ? c.textMuted
        : tone === "tint"
          ? c.tint
          : tone === "onTint"
            ? c.onTint
            : tone === "danger"
              ? c.danger
              : tone === "success"
                ? c.success
                : c.text;

  return (
    <Text
      maxFontSizeMultiplier={maxFontSizeMultiplier ?? VARIANT_MAX_SCALE[variant]}
      style={[styles[variant], { color }, center && styles.center, style]}
      {...rest}
    />
  );
}

const styles = StyleSheet.create({
  display: { fontSize: FontSize.xxxl, fontWeight: "800", letterSpacing: -0.5 },
  title: { fontSize: FontSize.xxl, fontWeight: "800", letterSpacing: -0.4 },
  subtitle: { fontSize: FontSize.lg, fontWeight: "700" },
  body: { fontSize: FontSize.md, fontWeight: "400", lineHeight: 23 },
  bodyStrong: { fontSize: FontSize.md, fontWeight: "600", lineHeight: 23 },
  label: { fontSize: FontSize.sm, fontWeight: "600", letterSpacing: 0.2 },
  caption: { fontSize: FontSize.xs, fontWeight: "500" },
  link: { fontSize: FontSize.md, fontWeight: "600" },
  center: { textAlign: "center" },
});
